import React, { useState, useEffect } from "react";
import { DatePicker } from "./date-picker";
import { UserForm } from "./user-form";
import { ConfirmationScreen } from "./confirmation-screen";
import { CategorySwitcher } from "./category-switcher";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { X, ChevronLeft, Headset } from "lucide-react";
import { format } from "date-fns";
import { fetchCategoryInfo } from "./api/booking";
import { Panel } from "primereact/panel";
import 'primereact/resources/themes/saga-blue/theme.css';  // Theme of your choice
import 'primereact/resources/primereact.min.css';          // Core PrimeReact styles
import 'primeicons/primeicons.css';                       // PrimeReact icons
import 'primeflex/primeflex.css';                         // PrimeFlex for grid and utilities

const CATEGORIES = [
  {
    id: 1,
    name: "health-insurance",
    title: "Health Insurance",
    subtitle: "Free 30-minute consultation",
    description:
      "Get personalized advice on choosing the right health insurance plan for you and your family.",
    questionPlaceholder: "Tell us about your health insurance needs...",
  },
  {
    id: 2,
    name: "investment",
    title: "Investment Planning",
    subtitle: "Free financial consultation",
    description:
      "Get expert guidance on investment strategies and portfolio management.",
    questionPlaceholder: "Tell us about your investment goals...",
  },
];

export function BookingWidget({ config, categoryCode, productCode, isVisible }) {
  const [step, setStep] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [currentCategoryId, setCurrentCategoryId] = useState(config.formId);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isVisible) return; // Only load data if the widget is visible

    const loadCategoryInfo = async () => {
      // console.log("Starting to load category info...");
      try {
        setIsLoading(true);
        const info = await fetchCategoryInfo(config.PY_API_URL, currentCategoryId, categoryCode, productCode);
        // console.log("Fetched category info:", info);
        setCategoryInfo(info.data);
        // console.log("Category info set:", info.data);

        if (info.data?.skip_slot) {
          setStep(2);
          // console.log("Skipping slot selection, moving to step 2");
        }
      } catch (err) {
        // console.error("Error fetching category info:", err);
        setError("Failed to load category information");
      } finally {
        setIsLoading(false);
        // console.log("isLoading set to false");
      }
    };

    loadCategoryInfo();
  }, [isVisible, currentCategoryId, categoryCode, productCode, config.PY_API_URL]);

  const handleCategoryChange = (newCategoryId) => {
    setCurrentCategoryId(newCategoryId);
    setStep(1);
    setSelectedSlot(null);
    setBookingDetails(null);
  };

  const handleDateSelection = (slot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const handleUserDetails = (details) => {
    setBookingDetails(details);
    setStep(3);
  };

  const handleEdit = () => {
    setStep(1);
  };

  // console.log("Rendering component with isLoading:", isLoading, "categoryInfo:", categoryInfo);

  if (isLoading) {
    return (
      <Card className="w-full max-w-6xl mx-auto">
        <div className="d-flex justify-content-center p-12">
          <div className="text-center">
            <div className="spinner-border text-primary mb-4"></div>
            <p className="text-gray-600">Loading booking information...</p>
          </div>
        </div>
      </Card>
    );
  }

  if (error || !categoryInfo) {
    return (
      <Card className="w-full max-w-6xl mx-auto">
        <div className="d-flex justify-content-center p-12">
          <div className="text-center">
            <p className="text-danger">
              Failed to load booking information. Please try again later.
            </p>
            <Button
              onClick={() => window.location.reload()}
              label="Retry"
              className="p-button-primary"
            />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="container-">
      <div className="row">
        <div className="col-12">
          <div className="row">
            {!config.hideHeaderSection && (
            <div className="col-12">
              <Panel header={categoryInfo?.page_title || "Book a call with expert"}>
                <div className="row">
                  <div className="col-1">
                    <div
                      style={{
                        background: "#6366f1",
                        width: "40px",
                        height: "40px",
                        textAlign: "center",
                        borderRadius: "10px",
                        color: "#fff",
                      }}
                    >
                      <Headset style={{ marginTop: "7px" }} size={24} />
                    </div>
                  </div>
                  <div className="col-7">
                    <div>
                      <h5 className="text-primary">{categoryInfo?.title}</h5>
                      <span className="text-muted">{categoryInfo?.subtitle}</span>
                    </div>
                  </div>
                  {/* <div className="col-3">
                    <CategorySwitcher
                      categories={CATEGORIES}
                      currentCategory={currentCategoryId}
                      onCategoryChange={handleCategoryChange}
                    />
                  </div> */}
                </div>
              </Panel>
            </div>
            )}

            {!categoryInfo?.skip_slot && 
            <div className="col-md-4 mt-10 booking-popup" style={{paddingRight: '0px'}}>
              <Card style={{padding: '0px'}}>
                <div>
                    {!categoryInfo?.skip_slot && 
                      <div className="p-3">
                        <h6 style={{fontSize: '18px'}}>{categoryInfo?.title}</h6>
                        <p className="text-muted">{categoryInfo?.description}</p>
                          <p>Your appointment details will appear here.</p>
                      </div>
                    }
                    {!categoryInfo?.skip_slot && selectedSlot && (
                    <div className="bg-light p-3 rounded">
                      <div className="d-flex justify-content-between mb-0">
                        <h6 style={{fontSize: '18px'}}>Selected Slot</h6>
                        {step === 2 && (
                          <i
                            className="pi pi-pencil"
                            onClick={handleEdit}
                            style={{ cursor: "pointer", fontSize: "12px" }}
                          ></i>
                        )}
                      </div>
                      <div className="text-muted">
                        <p>{format(selectedSlot.date, "EEEE, MMMM d, yyyy")}</p>
                        <p>{selectedSlot.time}</p>
                        <p>{selectedSlot.timezone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
            }
            
            
            {config.hideCardContainer ? (
              <div style={{width: '100%'}}>
                  {step === 1 && (
                    <DatePicker
                      onSelect={handleDateSelection}
                      categoryId={currentCategoryId}
                      config={config}
                    />
                  )}
                  {step === 2 && (
                    <UserForm
                      selectedSlot={selectedSlot}
                      onSubmit={handleUserDetails}
                      categoryInfo={categoryInfo}
                      config={config}
                    />
                  )}
                  {step === 3 && (
                    <ConfirmationScreen
                      slot={selectedSlot}
                      details={bookingDetails}
                      categoryInfo={categoryInfo}
                      config={config}
                    />
                  )}
                </div>
            ) : (
              <div className={`${categoryInfo?.skip_slot ? 'col-md-12' : 'col-md-8'}`}>
              <Card>
                <div>
                  {step === 1 && (
                    <DatePicker
                      onSelect={handleDateSelection}
                      categoryId={currentCategoryId}
                      config={config}
                    />
                  )}
                  {step === 2 && (
                    <UserForm
                      selectedSlot={selectedSlot}
                      onSubmit={handleUserDetails}
                      categoryInfo={categoryInfo}
                      config={config}
                    />
                  )}
                  {step === 3 && (
                    <ConfirmationScreen
                      slot={selectedSlot}
                      details={bookingDetails}
                      categoryInfo={categoryInfo}
                      config={config}
                    />
                  )}
                </div>
              </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
